import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

// Every query about a nurse sits under "nurse", so invalidating NURSE_QUERY_KEY refreshes all of them at once.
export const NURSE_QUERY_KEY = ["nurse"] as const;
export const NURSE_PROFILE_QUERY_KEY = [...NURSE_QUERY_KEY, "profile"] as const;
export const NURSE_SCHEDULE_QUERY_KEY = [...NURSE_QUERY_KEY, "schedule"] as const;

/**
 * Mark cached nurse queries stale after a write made outside the profile's own save — a shift or PTO changed
 * from a schedule cell, an HRIS PTO sync. The profile reads the nurse and its weeks only from these queries,
 * so a write that just patches `hospitalData` never reaches it.
 *
 * Pass `NURSE_QUERY_KEY` when the write touched everything about the nurse, or a narrower key when it touched
 * only that part. Invalidates by prefix, so it's a no-op when no profile is open.
 *
 * A standalone module so HospitalDataProvider and shared components can call it without importing the
 * profile's query hooks, which themselves read the provider.
 */
export const useInvalidateNurseQueries = () => {
	const queryClient = useQueryClient();
	return useCallback((queryKey: readonly string[]) => queryClient.invalidateQueries({ queryKey }), [queryClient]);
};
